import { getSchedulerType, type InterviewType } from '@/features/nylas-calendar-manage-connection-modal';
import { OutreachOrigin } from '@/shared/lib/outreach-origin';
import { QueryKey } from '@/types/query';
import { useQueryClient } from '@tanstack/react-query';
import { campaignsClient } from 'clients/CampaignsClient/CampaignsClient';
import { isAxiosError } from 'helpers/clientHelpers';
import { EventBus } from 'services/event-bus/EventBus';
import { CandidateEvent } from 'services/event-bus/EventBus.types';
import { BadRequestException } from 'shared/legacy/exceptions/BadRequestException';
import { useCandidateAction, WrappedHookParams } from './useCandidateAction';
import { WaitBeforeRemove } from './useWaitBeforeRemove';

export type RJCommHookCallbackParams = {
  data: {
    interviewType: InterviewType;
    candidate_first_name?: string | null;
    subject?: string;
    message?: string;
    improved_job_id?: number;
    isAutoSendInterviewInviteEnabled?: boolean;
    isWhatsAppOnlyFlow?: boolean;
  };
  options?: {
    waitBeforeRemoveFromList?: WaitBeforeRemove;
  };
};

const getChatId = () => {
  const chatParam = window.location.pathname.match(/\/chat\/(.+)$/)?.[1];
  const chatId = Number(chatParam);

  if (!isNaN(chatId)) {
    return chatId;
  }

  return null;
};

export const useRJComm = (params: WrappedHookParams) => {
  const queryClient = useQueryClient();

  return useCandidateAction<RJCommHookCallbackParams>(
    async ({ candidate, data }) => {
      await campaignsClient.startRJCommunicationHubPipeline({
        campaign_id: candidate.campaignId,
        es_person_id: candidate.es_person_id,
        ...(data.subject ? { subject: data.subject } : {}),
        ...(data.message ? { message: data.message } : {}),
        improved_job_id: data.improved_job_id,
        schedule_interview: data.isAutoSendInterviewInviteEnabled,
        scheduler_type: getSchedulerType(data.interviewType),
        origin: OutreachOrigin.rjCommunicationSingle,
        candidate_first_name: data.candidate_first_name,
      });
    },
    {
      onSuccess: ({ fcParams: { candidate, data }, gtm }) => {
        gtm.rjCommHubActivated();
        gtm.rjCommHuFirstReachoutSentEmail();

        const chatId = getChatId();
        if (chatId) {
          queryClient.setQueryData([QueryKey.useBestMatchesInfiniteQuery, chatId], (data: any) => {
            if (!data) return data;

            return {
              ...data,
              pages: data.pages.map((page: any) => ({
                ...page,
                candidates: page.candidates.filter((c: any) => c.esPersonId !== candidate.es_person_id),
              })),
            };
          });
        }

        EventBus.startTransaction(candidate.es_person_id, {
          name: candidate.firstName,
          esPersonId: candidate.es_person_id,
          status: candidate.status,
          labels: candidate.reasons,
        });

        EventBus.emit(
          CandidateEvent.Contacted,
          {
            esPersonId: candidate.es_person_id,
            contactMethod: data.isWhatsAppOnlyFlow ? 'whatsapp' : 'email',
            status: candidate.status,
            activeJobBoard: candidate.activeJobBoard,
          },
          candidate.es_person_id,
        );

        EventBus.commitTransaction(candidate.es_person_id);
      },
      onError: () => {
        return ({ data }) => {
          if (isAxiosError(data) && data.response?.status === 400) {
            throw new BadRequestException();
          }
          throw data;
        };
      },
    },
  )(params);
};
